// Fehler für Menschen (design-system §6.5): Was ist passiert? Was bedeutet das? Was kann ich tun?
// Der Server liefert bereits einen fertigen deutschen Satz; hier kommt dazu, was nur die Oberfläche weiß
// (welches Recht fehlt, dass nichts geändert wurde, dass eine Wiederholung gefahrlos ist).
import type { AnyActionContract } from '@shared/contract'
import { toAppError, type AppError } from '@shared/errors'
import { toast } from '../ui/Toast'
import { isUnreachableError, requestIdOf } from './client'
import { permissionLabel } from './permissions'

export interface ErrorText {
  /** ein Satz: was passiert ist */
  title: string
  /** was das bedeutet und was man tun kann */
  description?: string
  /** technische Kennung der Anfrage, klein und kopierbar */
  reference?: string
}

export function describeError(cause: unknown, contract?: AnyActionContract): ErrorText {
  const error: AppError = toAppError(cause)
  const reference = requestIdOf(error)
  const writes = contract !== undefined && contract.risk !== 'read'
  // keine Antwort von Merxo bzw. nur der Kante (502/504): Ob die Anfrage angekommen ist, steht nicht fest
  if (isUnreachableError(error)) {
    return {
      title: error.message,
      description: writes
        ? 'Ob die Änderung angekommen ist, steht nicht fest. „Erneut versuchen“ sendet sie sicher noch einmal – doppelt gebucht wird dabei nichts.'
        : undefined,
      reference,
    }
  }
  if (error.code === 'FORBIDDEN') {
    const permission = error.details?.permission
    if (typeof permission === 'string' && !permission.startsWith('@')) {
      return {
        title: `Für „${permissionLabel(permission)}“ fehlt Ihnen das Recht.`,
        description: 'Wenden Sie sich an die Person, die bei Ihnen die Rollen vergibt.',
        reference,
      }
    }
  }
  if (error.code === 'INTERNAL') {
    return {
      title: error.message,
      description: writes ? 'Es wurde nichts geändert.' : undefined,
      reference,
    }
  }
  return {
    title: error.message,
    description: writes && error.code !== 'VALIDATION' ? 'Es wurde nichts geändert.' : undefined,
    reference,
  }
}

/** Bleibender Toast in danger mit dem, was nicht geschehen ist; bei Verbindungsfehlern mit „Erneut versuchen“. */
export function toastError(cause: unknown, options: { contract?: AnyActionContract; retry?: () => void } = {}): void {
  const error = toAppError(cause)
  // die abgelaufene Sitzung zeigt die Hülle selbst (Anmeldeseite) – kein zusätzlicher Toast
  if (error.code === 'UNAUTHENTICATED') return
  const text = describeError(error, options.contract)
  toast.error(text.title, {
    id: options.contract ? `error:${options.contract.name}` : undefined,
    description: text.description,
    action:
      options.retry && (isUnreachableError(error) || error.retryable)
        ? { label: 'Erneut versuchen', onSelect: options.retry }
        : undefined,
  })
}
